import { useCallback, useEffect, useState } from "react";
import {
    Backdrop,
    Box,
    Button,
    Checkbox,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import CompanyAutocomplete from "../common/CompanyAutocomplete";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { DatePickerFilter } from "../common/DatePickerFilter";
import { CompanyShortDto } from "../../models/companies/companyShortDto";
import { WaybillsTableRowDto } from "../../models/waybills/waybillsTableRowDto";
import { waybillsService } from "../../services/waybillsService";
import { medicsService } from "../../services/medicsService";
import { TokenType } from "../../models/enums/tokenType";
import { cryptoProService } from "../../services/cryptoProService";
import {
    isNetworkError,
    NETWORK_ERROR_MESSAGE
} from "../../services/medicalCheckupSigningService";
import Spinner from "../common/Spinner";
import { ValidationErrorDialog } from "../common/ValidationErrorDialog";
import { WorkplaceNotReadyDialog } from "../common/WorkplaceNotReadyDialog";
import { WaybillTitleType, WaybillTitleTypeUtil } from "../../models/enums/waybillTitleType";
import { WaybillStatus, WaybillStatusUtil } from "../../models/enums/waybillStatus";
import { LookupDto } from "../../models/common/lookup.dto";
import { edoOperatorsService } from "../../services/edoOperatorsService";
import { DateUtils } from "../../helpers/dateUtils";
import { WaybillVerdict } from "../../models/enums/waybillVerdict";
import { EdoCompany } from "../../models/enums/edoCompany";
import ErrorAlert from "../common/ErrorAlert";

/** Значения option value для фильтра «Статус». */
type WaybillsStatusFilterKey = "" | "pending" | "error" | "approved" | "rejected";

/** Параметры фильтра «Статус» для API ЛК (статусы ПЛ + вердикт). */
function waybillsStatusFilterToApi(key: WaybillsStatusFilterKey): {
    statuses: WaybillStatus[] | null;
    verdicts: WaybillVerdict[] | null | undefined;
    unsignedOnly: boolean | undefined;
} {
    switch (key) {
        case "":
            return { statuses: null, verdicts: undefined, unsignedOnly: undefined };
        case "pending":
            return {
                statuses: [WaybillStatus.Pending],
                verdicts: undefined,
                unsignedOnly: true
            };
        case "error":
            return {
                statuses: [WaybillStatus.Error],
                verdicts: undefined,
                unsignedOnly: undefined
            };
        case "approved":
            return {
                statuses: [WaybillStatus.Processed],
                verdicts: [WaybillVerdict.Accepted],
                unsignedOnly: undefined
            };
        case "rejected":
            return {
                statuses: [WaybillStatus.Processed],
                verdicts: [WaybillVerdict.Rejected],
                unsignedOnly: undefined
            };
        default:
            return { statuses: null, verdicts: undefined, unsignedOnly: undefined };
    }
}

const filterFieldSx = { width: "100%", minWidth: 0 } as const;
const PAGE_REFRESH_INTERVAL = 60000;

const initialFilterState = {
    fromDate: null as string | null,
    toDate: null as string | null,
    company: null as CompanyShortDto | null,
    edoOperatorId: null as number | null,
    driverFio: "",
    titleType: "all" as "all" | WaybillTitleType,
    statusFilter: "" as WaybillsStatusFilterKey
};

function isFilterActive(f: typeof initialFilterState): boolean {
    return (
        f.fromDate != null ||
        f.toDate != null ||
        f.company != null ||
        f.edoOperatorId != null ||
        f.driverFio.trim() !== "" ||
        f.titleType !== "all" ||
        f.statusFilter !== ""
    );
}

export const WaybillsTable = () => {
    const [rows, setRows] = useState<WaybillsTableRowDto[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [loading, setLoading] = useState(false);
    const [busyWaybill, setBusyWaybill] = useState<string | null>(null);
    const [edoOperators, setEdoOperators] = useState<LookupDto[]>([]);
    const [signConfirm, setSignConfirm] = useState<{
        row: WaybillsTableRowDto;
        verdict: WaybillVerdict.Accepted | WaybillVerdict.Rejected;
    } | null>(null);

    const [signingBackdrop, setSigningBackdrop] = useState(false);
    const [signingSpinnerText, setSigningSpinnerText] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const [workplaceError, setWorkplaceError] = useState<string | null>(null);
    const [workplaceCheckLoading, setWorkplaceCheckLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [filter, setFilter] = useState(() => ({ ...initialFilterState }));

    const hasActiveFilters = isFilterActive(filter);

    const resetFilters = () => {
        setPage(0);
        setFilter({ ...initialFilterState });
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const titleTypes = filter.titleType === "all" ? null : [filter.titleType];
            const statusFilterParams = waybillsStatusFilterToApi(filter.statusFilter);
            const response = await waybillsService.getByFilter({
                // API ЛК ожидает DateOnly (yyyy-MM-dd); DatePickerFilter отдаёт ISO datetime.
                fromDate: filter.fromDate ? DateUtils.dateToISO(filter.fromDate) : null,
                toDate: filter.toDate ? DateUtils.dateToISO(filter.toDate) : null,
                companyId: filter.company?.id ?? null,
                driverFio: filter.driverFio.trim() ? filter.driverFio.trim() : null,
                titleTypes,
                edoOperatorId: filter.edoOperatorId,
                statuses: statusFilterParams.statuses,
                verdicts: statusFilterParams.verdicts,
                unsignedOnly: statusFilterParams.unsignedOnly,
                page,
                pageSize: rowsPerPage
            });

            setRows(response.data.data.items);
            setCount(response.data.data.count);
            setLoadError(null);
        } catch (e) {
            console.error(e);
            const err = e as { response?: { data?: { message?: string } }; message?: string };
            setLoadError(err.response?.data?.message ?? err.message ?? "Ошибка загрузки списка ЭПЛ.");
        } finally {
            setLoading(false);
        }
    }, [
        page,
        rowsPerPage,
        filter.fromDate,
        filter.toDate,
        filter.company,
        filter.edoOperatorId,
        filter.driverFio,
        filter.titleType,
        filter.statusFilter
    ]);

    useEffect(() => {
        void edoOperatorsService.getList().then(setEdoOperators).catch(() => setEdoOperators([]));
    }, []);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    useEffect(() => {
        const interval = setInterval(() => {
            void fetchData();
        }, PAGE_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [fetchData]);

    const handleSigningError = (e: unknown) => {
        console.error(e);
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        if (err.response) {
            setValidationError(err.response?.data?.message ?? err.message ?? "Ошибка валидации");
            return;
        }
        if (isNetworkError(e)) {
            setNetworkError(NETWORK_ERROR_MESSAGE);
            return;
        }
        setWorkplaceError(cryptoProService.normalizeError(e, "путевого листа"));
    };

    const signWaybill = async (row: WaybillsTableRowDto, verdict: WaybillVerdict.Accepted | WaybillVerdict.Rejected) => {
        setBusyWaybill(row.waybillNumber);
        setSigningBackdrop(true);
        setSigningSpinnerText("Подписание...");
        try {
            const cert = await medicsService.getMedicCertificate();
            if (!cert) {
                setValidationError("Не найден сертификат медика.");
                return;
            }

            const signPayload = {
                verdict,
                rejectReasons: verdict === WaybillVerdict.Rejected ? ["Недопуск по решению медика"] : [],
                digitalSignatureId: cert.id,
                edoCompany: row.edoCompany
            };

            if (cert.tokenType === TokenType.Cloud) {
                setSigningSpinnerText("Подписание облачным токеном...");
                await waybillsService.signWithCloudToken(row.waybillId, signPayload);
            } else {
                setSigningSpinnerText("Проверка рабочего места...");
                const workplace = await cryptoProService.checkWorkplaceReadiness();
                if (!workplace.isReady) {
                    setWorkplaceError(workplace.errors || "Рабочее место не готово к подписанию.");
                    return;
                }

                const onToken = await cryptoProService.isCertificateAvailableOnToken(cert.thumbprint);
                if (!onToken) {
                    setWorkplaceError(cryptoProService.normalizeError(new Error('Не найден ключ для подписания')));
                    return;
                }

                setSigningSpinnerText("Получение документа для подписи...");
                const hashData = await waybillsService.createHash(row.waybillId, signPayload);

                setSigningSpinnerText("Подписание электронной подписью...");
                const signature = await cryptoProService.sign(cert.thumbprint, hashData.xmlBase64, {
                    detached: true,
                    addUserCertificate: true
                });

                setSigningSpinnerText("Отправка на сервер...");
                await waybillsService.signWithPhysicalToken(row.waybillId, { signature });
            }

            await fetchData();
        } catch (e) {
            handleSigningError(e);
        } finally {
            setBusyWaybill(null);
            setSigningBackdrop(false);
            setSigningSpinnerText("");
        }
    };

    const handleSignConfirm = () => {
        if (!signConfirm) return;
        const { row, verdict } = signConfirm;
        setSignConfirm(null);
        void signWaybill(row, verdict);
    };

    function renderStatusBadge(status: WaybillStatus) {
        const label = WaybillStatusUtil.getLabel(status);
        let color = '#ec4a71';
        let backgroundColor = '#fce4ea';

        switch (status) {
            case WaybillStatus.Processed:
                color = '#5bba64';
                backgroundColor = '#edfbee';
                break;
            case WaybillStatus.Processing:
            case WaybillStatus.Matched:
                color = '#ff9800';
                backgroundColor = '#fff3e0';
                break;
            case WaybillStatus.Error:
                color = '#f44336';
                backgroundColor = '#ffebee';
                break;
            case WaybillStatus.Pending:
            default:
                color = '#7c7e92';
                backgroundColor = 'rgba(0, 0, 0, 0.06)';
                break;
        }

        return (
            <Box
                sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    color,
                    bgcolor: backgroundColor,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Box>
        );
    }

    return (
        <>
        <Box>
            {loadError && <ErrorAlert message={loadError} onClose={() => setLoadError(null)} />}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                    gap: 2,
                    mb: 2,
                    width: "100%",
                    alignItems: "end"
                }}
            >
                <DatePickerFilter
                    label="Начало периода"
                    name="fromDate"
                    value={filter.fromDate}
                    onChange={(value) => setFilter((prev) => ({ ...prev, fromDate: value ? String(value) : null }))}
                    style={{ ...filterFieldSx, marginTop: 4 }}
                />
                <DatePickerFilter
                    label="Конец периода"
                    name="toDate"
                    value={filter.toDate}
                    onChange={(value) => setFilter((prev) => ({ ...prev, toDate: value ? String(value) : null }))}
                    style={{ ...filterFieldSx, marginTop: 4 }}
                />
                <Box sx={{ minWidth: 0, width: "100%" }}>
                    <CompanyAutocomplete
                        company={filter.company}
                        onCompanyChange={(company) => setFilter((prev) => ({ ...prev, company }))}
                        label="Клиент"
                        style={{ ...filterFieldSx }}
                    />
                </Box>
                <TextField
                    select
                    label="Оператор ЭДО"
                    value={filter.edoOperatorId != null ? String(filter.edoOperatorId) : ""}
                    onChange={(e) => {
                        const v = e.target.value;
                        setFilter((prev) => ({
                            ...prev,
                            edoOperatorId: v === "" ? null : Number(v)
                        }));
                    }}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={filterFieldSx}
                >
                    <MenuItem value="">Все</MenuItem>
                    {edoOperators.map((op) => (
                        <MenuItem key={op.id} value={String(op.id)}>
                            {op.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="ФИО"
                    value={filter.driverFio}
                    onChange={(e) => setFilter((prev) => ({ ...prev, driverFio: e.target.value }))}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={filterFieldSx}
                />
                <TextField
                    select
                    label="Титул"
                    value={filter.titleType}
                    onChange={(e) =>
                        setFilter((prev) => ({
                            ...prev,
                            titleType: e.target.value as typeof prev.titleType
                        }))
                    }
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={filterFieldSx}
                >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value={WaybillTitleType.Title1}>Титул 2</MenuItem>
                    <MenuItem value={WaybillTitleType.Title5}>Титул 6</MenuItem>
                </TextField>
                <TextField
                    select
                    label="Статус"
                    value={filter.statusFilter}
                    onChange={(e) =>
                        setFilter((prev) => ({
                            ...prev,
                            statusFilter: (e.target.value || "") as WaybillsStatusFilterKey
                        }))
                    }
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={filterFieldSx}
                >
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="pending">Ожидает</MenuItem>
                    <MenuItem value="error">Ошибка обработки</MenuItem>
                    <MenuItem value="approved">Допуск</MenuItem>
                    <MenuItem value="rejected">Недопуск</MenuItem>
                </TextField>
                <Box sx={{ ...filterFieldSx, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    {hasActiveFilters ? (
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            fullWidth
                            startIcon={<Close fontSize="small" />}
                            onClick={resetFilters}
                            sx={(theme) => ({
                                textTransform: "none",
                                minHeight: 40,
                                mb: 0.5,
                                borderRadius: 2.5,
                                fontSize: theme.typography.body2.fontSize,
                                color: "text.primary",
                                borderColor: "rgba(0, 0, 0, 0.23)",
                                "&:hover": {
                                    borderColor: "rgba(0, 0, 0, 0.35)",
                                    bgcolor: "action.hover"
                                }
                            })}
                        >
                            Сбросить фильтры
                        </Button>
                    ) : null}
                </Box>
            </Box>

            <TableContainer sx={{ ".MuiTableCell-root": { py: 1.5 } }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    ID
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Номер
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Дата
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Оператор ЭДО
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    ФИО
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Дата рождения
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Клиент
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body1"
                                    sx={{ color: "#7c7e92" }}
                                    title="Клиент отмечен как входящий в группу Telemed"
                                >
                                    Telemed
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Титул
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Статус
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: "#7c7e92" }}>
                                    Допуск
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow
                                key={`${row.waybillId}-${row.waybillNumber}`}
                                sx={{
                                    bgcolor: index % 2 === 1 ? "rgba(0, 0, 0, 0.03)" : "transparent"
                                }}
                            >
                                <TableCell sx={{ fontWeight: 700 }}>{row.waybillId}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{row.waybillNumber}</TableCell>
                                <TableCell>{DateUtils.formatDate(row.waybillDate ?? null, "—")}</TableCell>
                                <TableCell>
                                    {row.edoCompany === EdoCompany.Undefined ? "—" : row.edoCompany}
                                </TableCell>
                                <TableCell>{row.driverFio}</TableCell>
                                <TableCell>{DateUtils.formatDate(row.driverBirthDate ?? null, "—")}</TableCell>
                                <TableCell>{row.clientName ?? "—"}</TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        checked={Boolean(row.isTelemedCompany)}
                                        disableRipple
                                        disabled
                                        size="small"
                                        sx={{ p: 0.5 }}
                                        inputProps={{
                                            "aria-label": "Клиент в группе Telemed",
                                            tabIndex: -1
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{WaybillTitleTypeUtil.getLabel(row.titleType)}</TableCell>
                                <TableCell>
                                    {row.status === WaybillStatus.Error ? (
                                        <Tooltip
                                            arrow
                                            enterDelay={250}
                                            title={row.errorMessage ?? "—"}
                                        >
                                            <Box component="span">{renderStatusBadge(row.status)}</Box>
                                        </Tooltip>
                                    ) : (
                                        renderStatusBadge(row.status)
                                    )}
                                </TableCell>
                                <TableCell>
                                    {WaybillStatusUtil.isActionable(row.status) ? (
                                        <Box display="flex" gap={1} alignItems="center">
                                            <IconButton
                                                size="small"
                                                aria-label="Допуск"
                                                disabled={busyWaybill === row.waybillNumber || loading || signingBackdrop}
                                                onClick={() =>
                                                    setSignConfirm({ row, verdict: WaybillVerdict.Accepted })
                                                }
                                                sx={(theme) => ({
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 3,
                                                    color: theme.palette.success.dark,
                                                    bgcolor: alpha(theme.palette.success.main, 0.14),
                                                    "&:hover": {
                                                        color: theme.palette.common.white,
                                                        bgcolor: theme.palette.success.main
                                                    },
                                                    "&.Mui-disabled": { opacity: 0.5 }
                                                })}
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Недопуск"
                                                disabled={busyWaybill === row.waybillNumber || loading || signingBackdrop}
                                                onClick={() =>
                                                    setSignConfirm({ row, verdict: WaybillVerdict.Rejected })
                                                }
                                                sx={(theme) => ({
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 3,
                                                    color: theme.palette.error.dark,
                                                    bgcolor: alpha(theme.palette.error.main, 0.14),
                                                    "&:hover": {
                                                        color: theme.palette.common.white,
                                                        bgcolor: theme.palette.error.main
                                                    },
                                                    "&.Mui-disabled": { opacity: 0.5 }
                                                })}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfirmDialog
                open={signConfirm != null}
                title={
                    signConfirm?.verdict === WaybillVerdict.Rejected
                        ? "Подписать ЭПЛ с НЕдопуском?"
                        : "Подписать ЭПЛ с допуском?"
                }
                content={null}
                onClose={() => setSignConfirm(null)}
                onConfirm={handleSignConfirm}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography>Всего: {count}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Строк на странице:"
                    labelDisplayedRows={({ from, to, count: c }) => `Показано ${from}-${to} из ${c}`}
                />
            </Box>
        </Box>

        <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
            open={signingBackdrop}
        >
            <Spinner text={signingSpinnerText} />
        </Backdrop>

        {validationError ? (
            <ValidationErrorDialog
                open
                message={validationError}
                onClose={() => setValidationError(null)}
            />
        ) : null}
        {networkError ? (
            <ValidationErrorDialog
                open
                title="Ошибка связи"
                message={networkError}
                onClose={() => setNetworkError(null)}
            />
        ) : null}
        {workplaceError ? (
            <WorkplaceNotReadyDialog
                open
                message={workplaceError}
                loading={workplaceCheckLoading}
                onClose={() => setWorkplaceError(null)}
                onRetry={async () => {
                    setWorkplaceCheckLoading(true);
                    try {
                        const wpStatus = await cryptoProService.checkWorkplaceReadiness();
                        if (wpStatus.isReady) {
                            setWorkplaceError(null);
                        } else {
                            setWorkplaceError(wpStatus.errors ?? "Рабочее место не готово к подписанию.");
                        }
                    } finally {
                        setWorkplaceCheckLoading(false);
                    }
                }}
            />
        ) : null}
        </>
    );
};
